import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Inbox,
  Search,
  Sparkles,
  Store,
  Calendar,
  CheckCircle2,
  Trash2,
  Edit,
  X,
  PhoneCall,
  MapPin,
  Clock,
  ArrowRight,
} from "lucide-react";
import { apiBoss } from "@/lib/api";

export default function BossLeadsPage() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  // Selected lead drawer / modal
  const [activeLead, setActiveLead] = useState(null);
  const [notesInput, setNotesInput] = useState("");
  const [followUpInput, setFollowUpInput] = useState("");
  const [convertingId, setConvertingId] = useState(null);

  const loadLeads = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (search.trim()) params.append("search", search.trim());
    if (statusFilter !== "All") params.append("status", statusFilter);

    apiBoss.get(`/boss/leads?${params.toString()}`)
      .then(({ data }) => setLeads(data))
      .catch(() => toast.error("Failed to load leads"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadLeads();
  }, [search, statusFilter]);

  const handleStatusChange = async (leadId, newStatus) => {
    try {
      await apiBoss.patch(`/boss/leads/${leadId}/status`, { status: newStatus });
      toast.success(`Status updated to ${newStatus}`);
      loadLeads();
    } catch (err) {
      toast.error("Failed to update status.");
    }
  };

  const handleSaveNotes = async () => {
    if (!activeLead) return;
    try {
      await apiBoss.patch(`/boss/leads/${activeLead.id}/status`, {
        notes: notesInput,
        follow_up_date: followUpInput || null,
      });
      toast.success("Lead notes & follow-up saved!");
      setActiveLead(null);
      loadLeads();
    } catch (err) {
      toast.error("Failed to save notes.");
    }
  };

  const handleDelete = async (leadId) => {
    if (!window.confirm("Are you sure you want to delete this lead?")) return;
    try {
      await apiBoss.delete(`/boss/leads/${leadId}`);
      toast.success("Lead deleted");
      loadLeads();
    } catch (err) {
      toast.error("Failed to delete lead.");
    }
  };

  // Convert Lead to Cafe Action
  const handleConvertToCafe = async (lead) => {
    if (!window.confirm(`Convert lead '${lead.cafe_name}' into a live Cafe record?`)) return;
    setConvertingId(lead.id);
    try {
      const { data } = await apiBoss.post(`/boss/leads/${lead.id}/convert`);
      toast.success(data.message || "Lead converted to Cafe successfully!");
      loadLeads();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to convert lead to cafe.");
    } finally {
      setConvertingId(null);
    }
  };

  return (
    <div className="space-y-8 max-w-7xl mx-auto">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-slate-800 pb-6">
        <div>
          <span className="text-xs font-mono uppercase tracking-[0.2em] text-primary font-semibold">Inquiries Pipeline</span>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white mt-1">Contact Forms & Leads</h1>
        </div>

        <div className="bg-slate-900 border border-slate-800 px-4 py-2 rounded-2xl text-xs font-mono text-slate-300">
          Total Leads: <b>{leads.length}</b>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-slate-900 border border-slate-800 p-4 rounded-3xl">
        <div className="relative flex-1 w-full">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search by owner name, cafe name, mobile, location..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full h-11 pl-11 pr-4 rounded-2xl bg-slate-950 border border-slate-800 text-sm text-white focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
        </div>

        <div className="flex gap-1.5 overflow-x-auto w-full sm:w-auto no-scrollbar">
          {["All", "New", "Contacted", "Interested", "In Progress", "Completed", "Not Interested", "Closed"].map((st) => (
            <button
              key={st}
              onClick={() => setStatusFilter(st)}
              className={`h-11 px-3.5 rounded-2xl text-xs font-semibold whitespace-nowrap transition-all border ${
                statusFilter === st
                  ? "bg-primary text-white border-primary"
                  : "bg-slate-950 text-slate-400 border-slate-800 hover:text-white"
              }`}
            >
              {st}
            </button>
          ))}
        </div>
      </div>

      {/* Leads Table */}
      <div className="bg-slate-900 border border-slate-800 rounded-3xl overflow-hidden shadow-xl">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs text-slate-300">
            <thead className="bg-slate-950 text-slate-400 uppercase font-mono border-b border-slate-800">
              <tr>
                <th className="py-4 px-6">Cafe & Owner</th>
                <th className="py-4 px-6">Mobile</th>
                <th className="py-4 px-6">Location</th>
                <th className="py-4 px-6 text-center">Tables</th>
                <th className="py-4 px-6">Status</th>
                <th className="py-4 px-6">Submitted Date</th>
                <th className="py-4 px-6 text-right">Actions</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-800/70">
              {loading ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-slate-500 font-mono">Loading leads...</td>
                </tr>
              ) : leads.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-slate-500 font-mono">No leads found.</td>
                </tr>
              ) : (
                leads.map((lead) => (
                  <tr key={lead.id} className="hover:bg-slate-800/40 transition-colors">
                    <td className="py-4 px-6">
                      <div className="font-bold text-white text-sm">{lead.cafe_name}</div>
                      <div className="text-slate-400 text-xs">{lead.name}</div>
                    </td>
                    <td className="py-4 px-6 font-mono text-slate-300">{lead.mobile}</td>
                    <td className="py-4 px-6">{lead.location}</td>
                    <td className="py-4 px-6 text-center font-mono font-bold text-white">{lead.table_count}</td>
                    <td className="py-4 px-6">
                      <select
                        value={lead.status}
                        onChange={(e) => handleStatusChange(lead.id, e.target.value)}
                        className={`px-3 py-1.5 rounded-full text-xs font-mono font-semibold bg-slate-950 border ${
                          lead.status === "New"
                            ? "border-primary text-primary"
                            : lead.status === "Completed"
                            ? "border-emerald-500 text-emerald-400"
                            : "border-slate-700 text-slate-300"
                        }`}
                      >
                        <option value="New">New</option>
                        <option value="Contacted">Contacted</option>
                        <option value="Interested">Interested</option>
                        <option value="In Progress">In Progress</option>
                        <option value="Completed">Completed</option>
                        <option value="Not Interested">Not Interested</option>
                        <option value="Closed">Closed</option>
                      </select>
                    </td>
                    <td className="py-4 px-6 font-mono text-[11px] text-slate-400">
                      {lead.created_at ? lead.created_at.slice(0, 10) : "-"}
                    </td>
                    <td className="py-4 px-6 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {/* Convert to Cafe Button */}
                        <button
                          onClick={() => handleConvertToCafe(lead)}
                          disabled={convertingId === lead.id || lead.status === "Completed"}
                          className="h-8 px-3 rounded-xl bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500 hover:text-white transition-all text-xs font-semibold flex items-center gap-1 disabled:opacity-40"
                          title="Convert Lead into Cafe Record"
                        >
                          <Store className="w-3.5 h-3.5" />
                          <span>{convertingId === lead.id ? "Converting..." : "Convert to Cafe"}</span>
                        </button>

                        {/* View/Edit Notes */}
                        <button
                          onClick={() => {
                            setActiveLead(lead);
                            setNotesInput(lead.notes || "");
                            setFollowUpInput(lead.follow_up_date || "");
                          }}
                          className="p-2 rounded-xl bg-slate-800 text-slate-300 hover:bg-slate-700 transition-colors"
                          title="View & Edit Lead Notes"
                        >
                          <Edit className="w-4 h-4" />
                        </button>

                        {/* Delete */}
                        <button
                          onClick={() => handleDelete(lead.id)}
                          className="p-2 rounded-xl bg-red-500/20 text-red-400 hover:bg-red-500 hover:text-white transition-colors"
                          title="Delete Lead"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* LEAD DETAILS & NOTES MODAL */}
      {activeLead && (
        <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-md z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl w-full max-w-lg p-6 space-y-6 shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <h3 className="font-heading text-xl font-bold text-white">Lead Details & Internal Notes</h3>
              <button onClick={() => setActiveLead(null)} className="p-1.5 rounded-xl text-slate-400 hover:bg-slate-800">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4 text-xs">
              <div className="bg-slate-950 p-4 rounded-2xl border border-slate-800 space-y-2">
                <div className="font-bold text-white text-base">{activeLead.cafe_name}</div>
                <div className="text-slate-300">Owner: <b>{activeLead.name}</b> • Mobile: <b className="font-mono">{activeLead.mobile}</b></div>
                <div className="text-slate-400">Location: {activeLead.location} • Tables: {activeLead.table_count}</div>
                {activeLead.message && (
                  <div className="pt-2 border-t border-slate-800 text-slate-300 italic">
                    "{activeLead.message}"
                  </div>
                )}
              </div>

              <div>
                <label className="block text-[10px] font-mono uppercase text-slate-400 mb-1">Follow-up Date</label>
                <input
                  type="date"
                  value={followUpInput}
                  onChange={(e) => setFollowUpInput(e.target.value)}
                  className="w-full h-11 px-3 rounded-xl bg-slate-950 border border-slate-800 text-sm text-white font-mono"
                />
              </div>

              <div>
                <label className="block text-[10px] font-mono uppercase text-slate-400 mb-1">Internal Notes</label>
                <textarea
                  rows={4}
                  placeholder="Add private sales notes, call history or custom menu requests..."
                  value={notesInput}
                  onChange={(e) => setNotesInput(e.target.value)}
                  className="w-full p-3 rounded-xl bg-slate-950 border border-slate-800 text-sm text-white"
                />
              </div>

              <div className="pt-2 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setActiveLead(null)}
                  className="h-10 px-4 rounded-xl border border-slate-800 text-slate-300"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveNotes}
                  className="h-10 px-5 rounded-xl bg-primary text-white font-semibold"
                >
                  Save Lead Notes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
